import { Children, Fragment, isValidElement, type ReactNode } from 'react';

function meaningfulChildren(node: ReactNode): ReactNode[] {
  return Children.toArray(node).filter((child) => {
    if (child == null || typeof child === 'boolean') return false;
    if (typeof child === 'string') return child.trim().length > 0;
    return true;
  });
}

/**
 * Pull a plain string suitable for the mobile navigation bar out of a page
 * header title tree. Prefers the first meaningful text node (typically inside
 * `PageTitle` / `Heading`) and ignores decorative siblings.
 */
export function extractNavTitle(node: ReactNode): string | undefined {
  if (node == null || typeof node === 'boolean') return undefined;

  if (typeof node === 'string' || typeof node === 'number') {
    const text = String(node).replace(/\s+/g, ' ').trim();
    return text || undefined;
  }

  if (Array.isArray(node)) {
    for (const child of node) {
      const text = extractNavTitle(child);
      if (text) return text;
    }
    return undefined;
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractNavTitle(node.props.children);
  }

  return undefined;
}

/**
 * True when the title tree is only a heading/string (no mobile-only controls,
 * guest intros, avatars, etc.). Used to omit an empty page-header band after
 * the large title collapses into the nav bar.
 */
export function isNavOnlyTitleTree(node: ReactNode): boolean {
  if (node == null || typeof node === 'boolean') return true;
  if (typeof node === 'string' || typeof node === 'number') return true;

  if (Array.isArray(node)) {
    const kids = meaningfulChildren(node);
    return kids.length === 1 && isNavOnlyTitleTree(kids[0]);
  }

  if (!isValidElement<{ children?: ReactNode }>(node)) return false;

  const type = node.type;
  if (type === Fragment) {
    return isNavOnlyTitleTree(node.props.children);
  }

  // Chakra Heading / our PageTitle / native headings — treat as nav-only leaves.
  if (typeof type === 'string') {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'].includes(type);
  }

  if (typeof type === 'function' || typeof type === 'object') {
    const name =
      (type as { displayName?: string; name?: string }).displayName ||
      (type as { name?: string }).name ||
      '';
    if (
      name === 'PageTitle' ||
      name === 'Heading' ||
      name.endsWith('Heading') ||
      name === 'Text'
    ) {
      return true;
    }
  }

  return false;
}
